import {
  WELL_COLS,
  WELL_ROWS,
  idx,
  idxCol,
  idxRow,
  type CellIndex,
  type GemKind,
  type Grid,
  type Piece,
} from './types';
import { getMatchMin } from './rules';
import { applyCapCheck, applyGravity, adjacentCells, at, clearCells, createGrid, pushUpAndFillBottom, restRow, rotatePiece, seedBoard, setCell } from './grid';
import { findFullRows, findRuns } from './match';
import { resolveWell, type ResolveReport } from './resolve';
import { BASE_GEM_VALUES, CASCADE_CAP, tierFor, tierGemMultiplier } from './effects';
import {
  DEFAULT_GEM_WEIGHTS,
  cloneWeights,
  pickWeightedGem,
  type GemWeights,
} from './gempool';
import {
  cascadeDepth,
  enchantById,
  resolveReports,
  shatterAdjacent,
  type EnchantId,
  type EnchantTrigger,
} from './enchant';
import {
  blockForTier,
  capDamageFor,
  relicSum,
  type RelicDef,
} from './relic';
import {
  dropStones,
  hexRandomColumn,
  scrambleGems,
  seededRng,
  shuffleInPlace,
  type Intent,
  type Rng,
} from './intent';

export const CAP_DAMAGE_PER_COLUMN = 8;

export type Phase = 'play' | 'enemy' | 'cap' | 'won' | 'lost';

/**
 * Colors random effects draw from. Starts as the four matchable gems at equal
 * weight; the shop's "cut a color" thins this for the rest of the run — deck
 * thinning, but for the board.
 */
/**
 * Every colour a match can be made from. Used by the enemy AI when it scores
 * an empty cell — the threat is not limited to what the run's gem pool
 * currently generates, because the player's cards, relics and Prism effects
 * can introduce any colour regardless of a cut.
 */
export const DEFAULT_GEM_POOL: readonly GemKind[] = ['ember', 'frost', 'venom', 'volt'];

export type PowerId = 'chain_reaction' | 'gem_socket' | 'metronome';

export interface ActivePower {
  readonly id: PowerId;
  readonly name: string;
  stacks: number;
}

/** How a card asks the player to aim. Drives the controller's state machine. */
export type TargetMode = 'none' | 'piece' | 'cell' | 'swap' | 'column' | 'color';

export interface CardContext {
  readonly combat: Combat;
  readonly grid: Grid;
  readonly rng: Rng;
  readonly column?: number | undefined;
  readonly rotation?: number | undefined;
  readonly cell?: CellIndex | undefined;
  readonly swapWith?: CellIndex | undefined;
  readonly color?: GemKind | undefined;
}

export interface CardDef {
  readonly id: string;
  readonly name: string;
  readonly cost: number;
  readonly target: TargetMode;
  readonly text: string;
  readonly piece?: Piece | undefined;
  /** Powers leave combat entirely once played — they are never drawn again. */
  readonly power?: boolean | undefined;
  /** Rarity, used by the reward screen at M6. */
  readonly rarity?: 'common' | 'uncommon' | 'rare' | undefined;
  /**
   * Groups cards that share the same piece geometry. Used by the reward
   * roller to avoid offering two variants of the same shape in one screen.
   * Optional — existing cards without it are unaffected.
   */
  readonly geometryId?: string | undefined;
  /**
   * May return a value that is handed to afterResolve. Pillar and Perfect
   * Clear need to condition on the resolve outcome, and the effect runs before
   * the resolve does.
   */
  effect(ctx: CardContext): number | void;
  afterResolve?(ctx: CardContext, report: ResolveReport, value: number | undefined): void;
}

/**
 * A card as it exists in a run: a definition plus whatever has been socketed
 * into this specific copy.
 *
 * Enchantments live HERE, never on `CardDef`. Two copies of T-Spin must be
 * able to coexist in one deck, one enchanted and one not, and stay distinct
 * through draw, discard, shuffle, combat and rewards. Mutating the definition
 * would make that impossible and would poison every later system that wants
 * per-instance state.
 */
export interface DeckCard {
  readonly def: CardDef;
  readonly enchants: readonly EnchantId[];
}

export interface CardInstance {
  readonly def: CardDef;
  readonly uid: number;
  readonly enchants: readonly EnchantId[];
}

/** Wrap a definition as an unenchanted deck entry. */
export function plain(def: CardDef): DeckCard {
  return { def, enchants: [] };
}

/** A deck entry carrying socketed enchantments. */
export function enchanted(def: CardDef, enchants: readonly EnchantId[]): DeckCard {
  return { def, enchants: [...enchants] };
}

function isDeckCard(x: CardDef | DeckCard): x is DeckCard {
  return (x as DeckCard).def !== undefined;
}

/** Accept bare definitions or deck entries; tests use the former. */
function toInstance(entry: CardDef | DeckCard, uid: number): CardInstance {
  if (isDeckCard(entry)) {
    return { def: entry.def, uid, enchants: [...entry.enchants] };
  }
  return { def: entry, uid, enchants: [] };
}

export interface EnemyDef {
  readonly id: string;
  readonly name: string;
  readonly maxHp: number;
  readonly pattern: readonly Intent[];
}

export interface PlayerState {
  hp: number;
  maxHp: number;
  block: number;
  energy: number;
  maxEnergy: number;
  drawPerTurn: number;
  /** Venom the enemies have put on the player. Ticks in the player's upkeep. */
  poison: number;
}

export interface EnemyState {
  readonly def: EnemyDef;
  readonly uid: number;
  hp: number;
  maxHp: number;
  block: number;
  poison: number;
  intent: Intent;
  patternIndex: number;
}

export type CombatEvent =
  | { kind: 'turnStart'; turn: number }
  | { kind: 'cardPlayed'; card: string }
  | { kind: 'resolve'; report: ResolveReport }
  | { kind: 'enemyResolve'; report: ResolveReport; enemy: number }
  | { kind: 'gemsDropped'; count: number }
  | { kind: 'playerPoisoned'; amount: number }
  | { kind: 'enemyDamaged'; enemy: number; amount: number; blocked: number }
  | { kind: 'playerDamaged'; amount: number; blocked: number }
  | { kind: 'playerBlocked'; amount: number }
  | { kind: 'energyGained'; amount: number }
  | { kind: 'enemyPoisoned'; enemy: number; amount: number }
  | { kind: 'enemyBlocked'; enemy: number; amount: number }
  | { kind: 'enemyActed'; enemy: number; intent: Intent }
  | { kind: 'stonesDropped'; count: number }
  | { kind: 'boardPushed'; rows: number; destroyed: number }
  | { kind: 'columnHexed'; column: number }
  | { kind: 'gemsScrambled'; count: number }
  | { kind: 'capDamage'; columns: number[]; amount: number }
  | { kind: 'enemyDied'; enemy: number }
  | { kind: 'combatEnd'; won: boolean }
  | { kind: 'enchantFired'; enchant: EnchantId; card: string; detail?: string };

export interface Combat {
  phase: Phase;
  turn: number;
  grid: Grid;
  player: PlayerState;
  enemies: EnemyState[];
  /** Enemy that receives untargeted damage. Falls back to leftmost living. */
  targetIndex: number;
  drawPile: CardInstance[];
  hand: CardInstance[];
  discardPile: CardInstance[];
  /** Powers land here once played. Removed from the combat, never redrawn. */
  exhaustedPile: CardInstance[];
  powers: ActivePower[];
  /** Run-long modifiers carried into this fight. */
  relics: RelicDef[];
  /** Colors random effects draw from. Thinnable at shops. */
  /**
   * Weights for naturally generated gems — seeding, Metronome, anything that
   * invents a colour. Owned by RunState and threaded into every fight.
   * Authored enemy pieces ignore it entirely.
   */
  gemWeights: GemWeights;
  /** Per-combat Volt meter. Survives across turns; see effects.ts. */
  voltRemainder: number;
  /** One free adjacent swap per turn, independent of the hand. */
  freeSwapUsed: boolean;
  log: CombatEvent[];
  rng: Rng;
}

export interface CombatOptions {
  readonly hp?: number;
  readonly maxEnergy?: number;
  readonly drawPerTurn?: number;
  readonly rng?: Rng;
  /** Seed the well with this many bottom rows at fight start. 0 = empty well. */
  readonly seedRows?: number;
  /** Relics carried in from the run. Copied, never aliased. */
  readonly relics?: readonly RelicDef[];
  /** Run-level gem composition. Copied; the run owns the original. */
  readonly gemWeights?: GemWeights;
}

function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  return shuffleInPlace(items.slice(), rng);
}

export function startCombat(
  deck: readonly (CardDef | DeckCard)[],
  enemies: readonly EnemyDef[],
  opts: CombatOptions = {},
): Combat {
  const rng = opts.rng ?? Math.random;
  const maxHp = opts.hp ?? 80;
  // Repeated enemies should form a formation, not act like synchronized
  // clones. Stagger only duplicates of the same definition; a unique enemy
  // still opens on the first entry in its authored pattern.
  const seenEnemyDefs = new Map<string, number>();

  // Relic riders on the player's core stats, applied once at combat start.
  const relics = opts.relics ?? [];
  let drawBonus = 0;
  let energyBonus = 0;
  for (const relic of relics) {
    drawBonus += relic.drawBonus ?? 0;
    energyBonus += relic.energyBonus ?? 0;
  }

  const combat: Combat = {
    phase: 'play',
    turn: 0,
    grid: createGrid(),
    player: {
      hp: maxHp,
      maxHp,
      block: 0,
      energy: 0,
      maxEnergy: (opts.maxEnergy ?? 3) + energyBonus,
      drawPerTurn: (opts.drawPerTurn ?? 5) + drawBonus,
      poison: 0,
    },
    enemies: enemies.map((def, i) => {
      const occurrence = seenEnemyDefs.get(def.id) ?? 0;
      seenEnemyDefs.set(def.id, occurrence + 1);
      const patternIndex = def.pattern.length > 0 ? occurrence % def.pattern.length : 0;
      return {
        def,
        uid: i + 1,
        hp: def.maxHp,
        maxHp: def.maxHp,
        block: 0,
        poison: 0,
        // Telegraphed from the first frame — duplicate enemies are staggered
        // across the same pattern so their combined turn is readable.
        intent: def.pattern[patternIndex] ?? { kind: 'block', amount: 0 },
        patternIndex,
      };
    }),
    targetIndex: 0,
    drawPile: shuffle(deck.map((entry, i) => toInstance(entry, i + 1)), rng),
    hand: [],
    discardPile: [],
    exhaustedPile: [],
    powers: [],
    relics: [...(opts.relics ?? [])],
    gemWeights: cloneWeights(opts.gemWeights ?? DEFAULT_GEM_WEIGHTS),
    voltRemainder: 0,
    freeSwapUsed: false,
    log: [],
    rng,
  };

  if ((opts.seedRows ?? 0) > 0) {
    seedBoard(combat.grid, opts.seedRows ?? 0, rng, combat.gemWeights);
    // Settle the seed. It can open with a full row or a run, and leaving that
    // pending means the player's very first card detonates something they
    // never saw happen. Combat must always begin on a board at rest.
    resolveWell(combat.grid, BASE_GEM_VALUES, 0);
  }

  // Relics that pre-place a piece. The player was promised this geometry, so
  // it must still be there when the first frame draws — placing it anywhere
  // and resolving let the seeded board eat it before anyone saw it.
  for (const relic of relics) {
    if (!relic.startPiece) continue;
    placeStartPiece(combat, relic.startPiece);
  }

  beginTurn(combat);
  return combat;
}

/**
 * Drop a promised starting piece somewhere it cannot immediately detonate.
 *
 * Searches columns and rotations for a resting spot that forms no run at all.
 * The seed is already settled, so the only risk is the piece joining two
 * neighbours — which this rules out rather than resolving away.
 *
 * Falls back to the first fitting spot if nowhere is safe, because a piece the
 * player can watch detonate is still better than one that never appears.
 */
function placeStartPiece(combat: Combat, piece: Piece): boolean {
  let second: { col: number; rot: number; row: number } | null = null;
  let last: { col: number; rot: number; row: number } | null = null;

  for (let col = 0; col < WELL_COLS; col++) {
    for (let rot = 0; rot < 4; rot++) {
      const placed = rotatePiece(piece, rot);
      const row = restRow(combat.grid, placed.cells, col);
      if (row === null) continue;

      const trial = combat.grid.slice();
      let fits = true;
      for (let i = 0; i < placed.cells.length; i++) {
        const offset = placed.cells[i];
        const kind = placed.kinds[i];
        if (!offset || !kind) continue;
        const r = row + offset[0];
        const c = col + offset[1];
        if (r < 0 || r >= WELL_ROWS || c < 0 || c >= WELL_COLS) {
          fits = false;
          break;
        }
        trial[idx(r, c)] = kind;
      }
      if (!fits) continue;

      // Tier 3: forms a run. Would detonate on the player's first card for no
      // reason they could have seen. Only used if nothing else fits.
      if (!last) last = { col, rot, row };
      if (findRuns(trial).length > 0) continue;

      // Tier 1: clean.
      if (findFullRows(trial).length === 0) {
        commitPiece(combat, placed, row, col);
        return true;
      }
      // Tier 2: completes a row. Visible and plannable, not silent.
      if (!second) second = { col, rot, row };
    }
  }

  const pick = second ?? last;
  if (!pick) return false;
  const placed = rotatePiece(piece, pick.rot);
  commitPiece(combat, placed, pick.row, pick.col);

  // Nowhere was clean. Clear the neighbours that formed the run rather than
  // the piece itself — the player was promised this geometry, and a few seeded
  // gems are a fair price for that promise. Bounded: the piece is a handful of
  // cells, so only its immediate surroundings can be involved.
  const mine = new Set<number>();
  for (const offset of placed.cells) mine.add(idx(pick.row + offset[0], pick.col + offset[1]));

  const doomed: number[] = [];
  for (const run of findRuns(combat.grid)) {
    for (const cell of run.cells) {
      if (!mine.has(cell)) doomed.push(cell);
    }
  }
  if (doomed.length > 0) {
    clearCells(combat.grid, doomed);
    applyGravity(combat.grid);
  }
  return second !== null;
}

function commitPiece(combat: Combat, placed: Piece, row: number, col: number): void {
  for (let i = 0; i < placed.cells.length; i++) {
    const offset = placed.cells[i];
    const kind = placed.kinds[i];
    if (!offset || !kind) continue;
    combat.grid[idx(row + offset[0], col + offset[1])] = kind;
  }
}

/** Begin upkeep and return poison damage dealt to the player this upkeep. */
function beginTurn(combat: Combat, into?: TurnStep[]): number {
  combat.turn += 1;
  // Block is a one-turn resource for the player; it never carries.
  combat.player.block = 0;
  combat.player.energy = combat.player.maxEnergy;
  combat.freeSwapUsed = false;

  // Poison ticks are a visible beat: the number has to leave the badge on
  // screen, not just decrement behind the player's back.
  const poisonSteps = tickPoison(combat);
  if (into) into.push(...poisonSteps);
  if (isOver(combat)) return 0;
  const poisonDamage = tickPlayerPoison(combat);
  if (isOver(combat)) return poisonDamage;

  // Metronome seeds the well before the player sees their hand, so the new
  // gem is part of the puzzle rather than a surprise mid-turn.
  const metronome = powerStacks(combat, 'metronome');
  for (let n = 0; n < metronome; n++) placeRandomGem(combat);

  drawCards(combat, combat.player.drawPerTurn);
  combat.phase = 'play';
  combat.log.push({ kind: 'turnStart', turn: combat.turn });
  return poisonDamage;
}

/** Poison resolves in upkeep, so an enemy it kills never takes its action. */
/**
 * Poison resolves in upkeep, so an enemy it kills never takes its action.
 * Returns one beat per enemy that actually ticked, so the player sees the
 * number leave rather than watching the badge silently decrement.
 */
function tickPoison(combat: Combat): TurnStep[] {
  const steps: TurnStep[] = [];
  for (const enemy of combat.enemies) {
    if (enemy.hp <= 0 || enemy.poison <= 0) continue;
    const dmg = enemy.poison;
    enemy.poison -= 1;
    combat.log.push({ kind: 'enemyPoisoned', enemy: enemy.uid, amount: dmg });
    damageEnemy(combat, enemy, dmg);
    steps.push(step('poisonTick', enemy.uid, combat, dmg));
  }
  checkEnd(combat);
  return steps;
}

/** Enemy Venom lands on the player and ticks at the start of their turn. */
function tickPlayerPoison(combat: Combat): number {
  const p = combat.player;
  if (p.poison <= 0) return 0;
  const dmg = p.poison;
  p.poison -= 1;
  const dealt = damagePlayer(combat, dmg);
  checkEnd(combat);
  return dealt;
}

export function damageEnemy(combat: Combat, enemy: EnemyState, amount: number): number {
  if (enemy.hp <= 0 || amount <= 0) return 0;
  const blocked = Math.min(enemy.block, amount);
  enemy.block -= blocked;
  const through = amount - blocked;
  enemy.hp = Math.max(0, enemy.hp - through);
  combat.log.push({ kind: 'enemyDamaged', enemy: enemy.uid, amount: through, blocked });
  if (enemy.hp === 0) combat.log.push({ kind: 'enemyDied', enemy: enemy.uid });
  return through;
}

export function damagePlayer(combat: Combat, amount: number): number {
  if (amount <= 0) return 0;
  const p = combat.player;
  const blocked = Math.min(p.block, amount);
  p.block -= blocked;
  const through = amount - blocked;
  p.hp = Math.max(0, p.hp - through);
  combat.log.push({ kind: 'playerDamaged', amount: through, blocked });
  return through;
}

export function addEnemyPoison(combat: Combat, enemy: EnemyState, amount: number): void {
  if (enemy.hp <= 0 || amount <= 0) return;
  enemy.poison += amount;
  combat.log.push({ kind: 'enemyPoisoned', enemy: enemy.uid, amount });
}

function checkEnd(combat: Combat): void {
  if (combat.phase === 'won' || combat.phase === 'lost') return;
  if (combat.player.hp <= 0) {
    combat.phase = 'lost';
    combat.log.push({ kind: 'combatEnd', won: false });
    return;
  }
  if (combat.enemies.every(e => e.hp <= 0)) {
    combat.phase = 'won';
    combat.log.push({ kind: 'combatEnd', won: true });
  }
}

/**
 * True once combat has finished. Reads the phase through a widened local so
 * TypeScript's control-flow narrowing of a freshly assigned phase cannot
 * "prove" the comparison impossible — damagePlayer/executeIntent mutate the
 * phase behind the compiler's back.
 */
function isOver(combat: Combat): boolean {
  const phase: Phase = combat.phase;
  return phase === 'won' || phase === 'lost';
}

function isLost(combat: Combat): boolean {
  const phase: Phase = combat.phase;
  return phase === 'lost';
}

export function currentTarget(combat: Combat): EnemyState | null {
  const chosen = combat.enemies[combat.targetIndex];
  if (chosen && chosen.hp > 0) return chosen;
  return combat.enemies.find(e => e.hp > 0) ?? null;
}

export function setTarget(combat: Combat, index: number): void {
  if (index >= 0 && index < combat.enemies.length) combat.targetIndex = index;
}

// --- powers ---------------------------------------------------------------

export function addPower(combat: Combat, id: PowerId, name: string, stacks = 1): void {
  const existing = combat.powers.find(p => p.id === id);
  if (existing) {
    existing.stacks += stacks;
    return;
  }
  combat.powers.push({ id, name, stacks });
}

export function powerStacks(combat: Combat, id: PowerId): number {
  return combat.powers.find(p => p.id === id)?.stacks ?? 0;
}

/** Drop one naturally generated gem into a random open column, by weight. */
export function placeRandomGem(combat: Combat): CellIndex | null {
  const open: number[] = [];
  for (let c = 0; c < WELL_COLS; c++) if (combat.grid[idx(0, c)] === null) open.push(c);
  const kind = pickWeightedGem(combat.rng, combat.gemWeights);
  if (open.length === 0 || kind === null) return null;
  const col = open[Math.floor(combat.rng() * open.length)];
  if (col === undefined) return null;
  const row = restRow(combat.grid, SINGLE_CELL, col);
  if (row === null) return null;
  setCell(combat.grid, row, col, kind);
  return idx(row, col);
}

const SINGLE_CELL: readonly (readonly [number, number])[] = [[0, 0]];

export function drawCards(combat: Combat, count: number): CardInstance[] {
  const drawn: CardInstance[] = [];
  for (let i = 0; i < count; i++) {
    if (combat.drawPile.length === 0) {
      if (combat.discardPile.length === 0) break;
      combat.drawPile = shuffle(combat.discardPile, combat.rng);
      combat.discardPile = [];
    }
    const card = combat.drawPile.pop();
    if (!card) break;
    combat.hand.push(card);
    drawn.push(card);
  }
  return drawn;
}

/**
 * Route a resolve report into combat state, wave by wave.
 *
 * Wave-by-wave rather than as one total so a target can die mid-cascade and
 * the overflow spills to the next living enemy — which is the difference
 * between a cascade that overkills a Shardling and one that carries on into
 * the Golem behind it.
 */
function applyResolve(combat: Combat, report: ResolveReport): void {
  combat.voltRemainder = report.voltRemainder;

  // Relic riders that apply flatly to every wave.
  const burstBonus = relicSum(combat.relics, 'rowBurstBonus');
  const stoneRowDamage = relicSum(combat.relics, 'stoneRowDamage');

  for (const wave of report.waves) {
    if (wave.frostBlock > 0) {
      combat.player.block += wave.frostBlock;
      combat.log.push({ kind: 'playerBlocked', amount: wave.frostBlock });
    }
    if (wave.energyGained > 0) {
      combat.player.energy += wave.energyGained;
      combat.log.push({ kind: 'energyGained', amount: wave.energyGained });
    }

    // A Surge/Aether Venom run poisons everyone; otherwise it follows target.
    if (wave.poison > 0) {
      if (wave.venomHitsAll) {
        for (const enemy of combat.enemies) addEnemyPoison(combat, enemy, wave.poison);
      } else {
        const target = currentTarget(combat);
        if (target) addEnemyPoison(combat, target, wave.poison);
      }
    }

    // Surge Ember hits every living enemy. Row burst is deliberately NOT
    // routed with it — that flat table always follows the target, which keeps
    // the burst from turning a Surge into a board wipe.
    if (wave.emberDamage > 0) {
      if (wave.emberHitsAll) {
        for (const enemy of combat.enemies) {
          if (enemy.hp > 0) damageEnemy(combat, enemy, wave.emberDamage);
        }
      } else {
        const target = currentTarget(combat);
        if (target) damageEnemy(combat, target, wave.emberDamage);
      }
    }

    // Lead Weight adds a flat rider to the burst; Mason's Trowel makes the
    // Stone that a row destroyed bite back. Both only apply when a row
    // actually cleared — without that gate the weight would pay out on every
    // ordinary 3-match.
    const burst =
      wave.rowsCleared.length > 0
        ? wave.rowBurst + burstBonus + wave.stonesInRows * stoneRowDamage
        : 0;
    if (burst > 0) {
      const target = currentTarget(combat);
      if (target) damageEnemy(combat, target, burst);
    }

    // Rune of the Bulwark and friends: block keyed off the tier reached.
    if (wave.tier !== null) {
      const gift = blockForTier(combat.relics, wave.tier);
      if (gift > 0) {
        combat.player.block += gift;
        combat.log.push({ kind: 'playerBlocked', amount: gift });
      }
    }

    checkEnd(combat);
  }

  // Powers that key off the resolve as a whole rather than any single wave.
  let rowsCleared = 0;
  let deepest = 0;
  for (const wave of report.waves) {
    rowsCleared += wave.rowsCleared.length;
    if (wave.wave > deepest) deepest = wave.wave;
  }

  const socket = powerStacks(combat, 'gem_socket');
  if (socket > 0 && rowsCleared > 0) {
    const block = 4 * socket * rowsCleared;
    combat.player.block += block;
    combat.log.push({ kind: 'playerBlocked', amount: block });
  }

  const chain = powerStacks(combat, 'chain_reaction');
  if (chain > 0 && deepest >= 3) {
    const energy = 2 * chain;
    combat.player.energy += energy;
    combat.log.push({ kind: 'energyGained', amount: energy });
  }
}

export function resolveBoard(combat: Combat): ResolveReport {
  const cap = CASCADE_CAP + relicSum(combat.relics, 'cascadeCapBonus');
  const report = resolveWell(combat.grid, BASE_GEM_VALUES, combat.voltRemainder, cap);
  combat.log.push({ kind: 'resolve', report });
  applyResolve(combat, report);
  checkEnd(combat);
  return report;
}

/** Would placing `kind` at (r, c) complete a run of matchMin? */
function createsRunAt(grid: Grid, r: number, c: number, kind: GemKind): boolean {
  const matchMin = getMatchMin();
  const same = (rr: number, cc: number) => at(grid, rr, cc) === kind;

  let left = 0;
  while (same(r, c - left - 1)) left++;
  let right = 0;
  while (same(r, c + right + 1)) right++;
  if (left + 1 + right >= matchMin) return true;

  let up = 0;
  while (same(r - up - 1, c)) up++;
  let down = 0;
  while (same(r + down + 1, c)) down++;
  if (up + 1 + down >= matchMin) return true;

  return false;
}

/**
 * Empty cells where some colour would complete a match. This is the enemy's
 * read on the player's board: each one is a square the player is one drop away
 * from cashing. Occupying one is the Connect 4 block.
 */
function playerThreats(grid: Grid): number {
  let n = 0;
  for (let r = 0; r < WELL_ROWS; r++) {
    for (let c = 0; c < WELL_COLS; c++) {
      if ((grid[idx(r, c)] ?? null) !== null) continue;
      for (const kind of DEFAULT_GEM_POOL) {
        if (createsRunAt(grid, r, c, kind)) {
          n++;
          break;
        }
      }
    }
  }
  return n;
}

/**
 * Pick where the enemy drops its piece. It weighs two goals the way a Connect 4
 * opponent weighs a winning square against a blocking square:
 *  - attack: the drop completes a match, which lands on the player;
 *  - block:  the drop occupies a square the player needs, spoiling their setup.
 * Attack is weighted higher, so the enemy takes the kill when it can and
 * defends otherwise. Every column and rotation is tried.
 */
function chooseEnemyPlacement(
  combat: Combat,
  piece: Piece,
): { column: number; rotation: number } | null {
  const before = playerThreats(combat.grid);
  let best: { column: number; rotation: number } | null = null;
  let bestScore = -Infinity;

  for (let rotation = 0; rotation < 4; rotation++) {
    const placed = rotatePiece(piece, rotation);
    for (let column = 0; column < WELL_COLS; column++) {
      const row = restRow(combat.grid, placed.cells, column);
      if (row === null) continue;

      const trial = combat.grid.slice();
      placed.cells.forEach(([dr, dc], i) => {
        const kind = placed.kinds[i];
        if (kind !== undefined) setCell(trial, row + dr, column + dc, kind);
      });

      const report = resolveWell(trial, BASE_GEM_VALUES, 0);
      const attack = report.totalDamage;
      const blocked = Math.max(0, before - playerThreats(trial));
      const score = attack * 4 + blocked * 2;
      if (score > bestScore) {
        bestScore = score;
        best = { column, rotation };
      }
    }
  }
  return best;
}

/**
 * Route an enemy-caused resolve to the player: their match is the player's
 * problem. Enemy Frost shields the enemy; enemy Venom poisons the player.
 */
function applyEnemyResolve(combat: Combat, report: ResolveReport, source: EnemyState): void {
  for (const wave of report.waves) {
    const dmg = wave.emberDamage + wave.rowBurst;
    if (dmg > 0) damagePlayer(combat, dmg);
    if (wave.frostBlock > 0) {
      source.block += wave.frostBlock;
      combat.log.push({ kind: 'enemyBlocked', enemy: source.uid, amount: wave.frostBlock });
    }
    if (wave.poison > 0) {
      combat.player.poison += wave.poison;
      combat.log.push({ kind: 'playerPoisoned', amount: wave.poison });
    }
    // Enemy Volt is inert: enemies have no energy pool to charge.
    checkEnd(combat);
  }
}

/**
 * Resolve the well after an enemy moved it. Same cascade as the player's, but
 * the effects point the other way — the enemy's Ember burns the player. The
 * player's Volt meter is untouched, so an enemy drop cannot bank the player
 * energy, and its remainder is deliberately not written back.
 */
export function resolveBoardEnemy(combat: Combat, source: EnemyState): ResolveReport {
  const report = resolveWell(combat.grid, BASE_GEM_VALUES, 0);
  combat.log.push({ kind: 'enemyResolve', report, enemy: source.uid });
  applyEnemyResolve(combat, report, source);
  checkEnd(combat);
  return report;
}

/** Drop the enemy's piece at its chosen column, then settle the board. */
/**
 * One visible beat of the enemy turn.
 *
 * Combat resolves the whole turn synchronously — the board is already in its
 * final state by the time the renderer sees it. Each step carries a snapshot
 * of the board as it stood AFTER that beat, so playback can walk the turn one
 * event at a time instead of teleporting to the end. Same shape as the cascade
 * playback, one level up.
 */
export interface TurnStep {
  readonly kind:
    | 'capDamage'
    | 'enemyAttack'
    | 'enemyBlock'
    | 'pieceDrop'
    | 'stoneDrop'
    | 'boardPush'
    | 'hexColumn'
    | 'scramble'
    | 'enemyResolve'
    | 'poisonTick'
    | 'playerPoison';
  readonly enemyUid: number;
  readonly amount: number;
  /**
   * How much of `amount` Block absorbed. Carried separately so the HUD can
   * show "6 promised, 2 landed" instead of an unexplained smaller number.
   */
  readonly blocked: number;
  /** Seconds this beat holds. Piece drops are deliberately the slowest. */
  readonly dur: number;
  /** Board after this beat. */
  readonly grid: Grid;
  /**
   * Everything else the screen shows, frozen at the moment this beat began.
   * Without it the live combat state leaks through — the panel would already
   * read `ATK 4 x2` while the `ATK 6` is still landing.
   */
  readonly view: TurnView;
  /** For pieceDrop: the cells the piece landed in, drawn as a falling piece. */
  readonly cells?: readonly CellIndex[] | undefined;
  readonly kinds?: readonly GemKind[] | undefined;
  /** For enemyResolve: the cascade to replay over `grid` (the landed board). */
  readonly report?: ResolveReport | undefined;
}

/** A single enemy's state as the screen should show it during a beat. */
export interface EnemyView {
  readonly uid: number;
  readonly name: string;
  readonly hp: number;
  readonly maxHp: number;
  readonly block: number;
  readonly poison: number;
  /** The intent being performed — the NEXT one must not appear yet. */
  readonly intent: Intent;
}

/**
 * The displayable slice of combat, captured per beat. Deliberately a value
 * snapshot rather than a reference: combat keeps mutating underneath playback,
 * and a reference would render the future.
 */
export interface TurnView {
  readonly playerHp: number;
  readonly playerMaxHp: number;
  readonly playerBlock: number;
  readonly playerPoison: number;
  readonly playerEnergy: number;
  readonly turn: number;
  readonly phase: Phase;
  readonly enemies: readonly EnemyView[];
  /** The hand is discarded during the enemy phase, so it must not draw. */
  readonly showHand: boolean;
}

/** Freeze everything the screen shows right now. */
export function snapshotView(combat: Combat): TurnView {
  return {
    playerHp: combat.player.hp,
    playerMaxHp: combat.player.maxHp,
    playerBlock: combat.player.block,
    playerPoison: combat.player.poison,
    playerEnergy: combat.player.energy,
    turn: combat.turn,
    phase: combat.phase,
    // The enemy phase is not the player's: their grip is already in the pile.
    showHand: combat.phase === 'play',
    enemies: combat.enemies.map(e => ({
      uid: e.uid,
      name: e.def.name,
      hp: e.hp,
      maxHp: e.maxHp,
      block: e.block,
      poison: e.poison,
      // Static pattern data, so this reference is safe to hold.
      intent: e.intent,
    })),
  };
}

/** Durations in seconds. Piece placement is the long beat on purpose. */
const STEP_DUR = {
  capDamage: 0.6,
  /**
   * Long enough to watch the projectile arrive and still read the number. At
   * 0.4s the impact and the float were over before the eye got there.
   */
  enemyAttack: 0.62,
  enemyBlock: 0.42,
  /**
   * An enemy piece must arrive markedly slower than the player's own cascade
   * falls. It is a threat being telegraphed — the player has to watch it come,
   * read where it is going, and decide whether to care. A future relic
   * direction is knocking an enemy's piece off course mid-drop, which only
   * works if the drop is long enough to act on.
   */
  pieceDrop: 1.6,
  stoneDrop: 1.35,
  boardPush: 0.75,
  hexColumn: 0.6,
  scramble: 0.55,
  enemyResolve: 0.55,
  poisonTick: 0.42,
  playerPoison: 0.55,
} as const;

function step(
  kind: TurnStep['kind'],
  enemyUid: number,
  combat: Combat,
  amount = 0,
  extra?: {
    cells?: readonly CellIndex[];
    kinds?: readonly GemKind[];
    grid?: Grid;
    report?: ResolveReport;
    /** Override the default beat length. Used to make a flurry read as one. */
    dur?: number;
    blocked?: number;
  },
): TurnStep {
  return {
    kind,
    enemyUid,
    amount,
    blocked: extra?.blocked ?? 0,
    dur:
      extra?.dur ??
      (kind === 'enemyResolve'
        ? STEP_DUR.enemyResolve * Math.max(1, extra?.report?.waves.length ?? 1)
        : STEP_DUR[kind]),
    grid: (extra?.grid ?? combat.grid).slice(),
    view: snapshotView(combat),
    cells: extra?.cells,
    kinds: extra?.kinds,
    report: extra?.report,
  };
}

function enemyDropPiece(
  combat: Combat,
  enemy: EnemyState,
  piece: Piece,
): TurnStep[] {
  const choice = chooseEnemyPlacement(combat, piece);
  if (!choice) return [];
  const before = combat.grid.slice();
  if (!placePieceAt(combat, piece, choice.column, choice.rotation)) return [];

  // Diff the board to find exactly where the piece landed. Doing it this way
  // rather than recomputing the rest position means the step cannot drift from
  // what actually happened.
  const cells: CellIndex[] = [];
  const kinds: GemKind[] = [];
  for (let i = 0; i < before.length; i++) {
    if (before[i] === null && combat.grid[i] !== null) {
      cells.push(i);
      const kind = combat.grid[i];
      if (kind) kinds.push(kind);
    }
  }

  const steps: TurnStep[] = [];
  if (cells.length > 0) {
    combat.log.push({ kind: 'gemsDropped', count: cells.length });
    steps.push(step('pieceDrop', enemy.uid, combat, cells.length, { cells, kinds }));
  }

  const landedGrid = combat.grid.slice();
  const hpBefore = combat.player.hp;
  const report = resolveBoardEnemy(combat, enemy);
  // A no-op resolve is not a visible event. Previously this empty beat, plus
  // the missing match animation on real resolves, made drops appear to deal
  // arbitrary damage.
  if (report.waves.length > 0) {
    steps.push(step('enemyResolve', enemy.uid, combat, hpBefore - combat.player.hp, {
      grid: landedGrid,
      report,
    }));
  }
  return steps;
}

/** The tetromino an intent will drop, if any (unwrapping a multi). */
export function intentDropPiece(intent: Intent): Piece | null {
  if (intent.kind === 'dropPiece') return intent.piece;
  if (intent.kind === 'multi') {
    for (const sub of intent.intents) {
      const p = intentDropPiece(sub);
      if (p) return p;
    }
  }
  return null;
}

/**
 * Where the enemy would drop *right now*, for the telegraph ghost. The AI is
 * deterministic given the board, so revealing its answer turns the drop from a
 * gotcha into a square the player can plan around — the same trick the outcome
 * preview plays on cascades.
 */
export function previewEnemyDrop(
  combat: Combat,
  enemy: EnemyState,
): { column: number; rotation: number } | null {
  const piece = intentDropPiece(enemy.intent);
  if (!piece) return null;
  return chooseEnemyPlacement(combat, piece);
}

/** Drop a piece into a column at a rotation. False if it cannot be placed. */
export function placePieceAt(
  combat: Combat,
  piece: Piece,
  column: number,
  rotation = 0,
): boolean {
  const placed = rotatePiece(piece, rotation);
  const row = restRow(combat.grid, placed.cells, column);
  if (row === null) return false;
  placed.cells.forEach(([dr, dc], i) => {
    const kind = placed.kinds[i];
    if (kind !== undefined) setCell(combat.grid, row + dr, column + dc, kind);
  });
  return true;
}

export interface PlayTarget {
  readonly column?: number | undefined;
  readonly rotation?: number | undefined;
  readonly cell?: CellIndex | undefined;
  readonly swapWith?: CellIndex | undefined;
  readonly color?: GemKind | undefined;
}

export function canPlay(combat: Combat, card: CardInstance): boolean {
  return (
    combat.phase === 'play' &&
    combat.player.energy >= card.def.cost &&
    combat.hand.includes(card)
  );
}

export function playCard(
  combat: Combat,
  card: CardInstance,
  target: PlayTarget = {},
): ResolveReport | null {
  if (!canPlay(combat, card)) return null;

  combat.player.energy -= card.def.cost;
  const i = combat.hand.indexOf(card);
  if (i >= 0) combat.hand.splice(i, 1);
  combat.log.push({ kind: 'cardPlayed', card: card.def.name });

  const ctx: CardContext = {
    combat,
    grid: combat.grid,
    rng: combat.rng,
    column: target.column,
    rotation: target.rotation,
    cell: target.cell,
    swapWith: target.swapWith,
    color: target.color,
  };
  const gridBefore = combat.grid.slice();
  const raw = card.def.effect(ctx);
  const value = typeof raw === 'number' ? raw : undefined;

  // Card effects that remove cells (Junk Shot, Shatter, Sweeper, etc.) leave
  // holes. Anything above those holes must fall before the resolver checks
  // for matches — otherwise gems float in mid-air.
  applyGravity(combat.grid);

  // What this card newly occupied. HEURISTIC, not a definition: it works
  // because every current placement fills empty cells. Future mechanics that
  // overwrite — Heavy crushing a cell, Wild converting one, a hazard replacing
  // another — need the placement itself to report what it landed on. When that
  // happens, change here; do not let the diff quietly become what "this card's
  // geometry" means.
  const placed = newlyOccupied(gridBefore, combat.grid);

  // Shatter fires BEFORE the resolve so what it destroys can cascade. An
  // enchantment that reads the board has to be able to change it first.
  applyPreResolveEnchants(combat, card, placed);

  // Always resolve. A card that left the grid alone yields zero waves, so this
  // is free for powers and guarantees no effect can forget to cascade.
  const report = resolveBoard(combat);
  card.def.afterResolve?.(ctx, report, value);
  if (card.def.power === true) combat.exhaustedPile.push(card);
  else combat.discardPile.push(card);

  // Echo needs the card to be in the discard pile to move it, so it runs
  // after the pile push.
  applyPostResolveEnchants(combat, card, report);
  checkEnd(combat);
  return report;
}

/**
 * Cells the effect newly occupied: null on the way in, filled on the way out.
 * See the note at the call site — this is a placement heuristic, not the
 * definition of a card's geometry.
 */
function newlyOccupied(before: Grid, after: Grid): CellIndex[] {
  const placed: CellIndex[] = [];
  for (let i = 0; i < before.length; i++) {
    if (before[i] === null && after[i] !== null) placed.push(i);
  }
  return placed;
}

/** Enchantments that act on the board or the enemies. Fire before the resolve. */
function applyPreResolveEnchants(
  combat: Combat,
  card: CardInstance,
  placed: readonly CellIndex[],
): void {
  if (card.enchants.length === 0 || placed.length === 0) return;

  if (card.enchants.includes('shatter')) {
    const doomed = shatterAdjacent(combat.grid, placed);
    if (doomed.length > 0) {
      combat.log.push({
        kind: 'enchantFired',
        enchant: 'shatter',
        card: card.def.name,
        detail: `${doomed.length} stone`,
      });
    }
  }

  // Burn counts the Ember the piece actually landed on and bites now — the
  // damage lands before any match the piece creates can clear those gems.
  if (card.enchants.includes('burn')) {
    const emberCount = placed.filter(i => combat.grid[i] === 'ember').length;
    if (emberCount > 0) {
      const dmg = emberCount * 2;
      const target = currentTarget(combat);
      if (target) {
        damageEnemy(combat, target, dmg);
        combat.log.push({
          kind: 'enchantFired',
          enchant: 'burn',
          card: card.def.name,
          detail: `${emberCount} ember × 2 = ${dmg} damage`,
        });
      }
    }
  }
}

/** Enchantments that read the resolve. Fire after it. */
function applyPostResolveEnchants(
  combat: Combat,
  card: CardInstance,
  report: ResolveReport,
): void {
  if (card.enchants.length === 0) return;

  if (card.enchants.includes('resonance')) {
    if (cascadeDepth(report.waves) >= 2) {
      combat.player.energy += 2;
      combat.log.push({
        kind: 'enchantFired',
        enchant: 'resonance',
        card: card.def.name,
        detail: '+2 energy',
      });
    }
  }

  // Echo answers whichever triggers its definition declares. Today that is
  // match and row clear; the vocabulary is explicit so a later stone can
  // answer one without the other.
  if (card.enchants.includes('echo')) {
    const echoDef = enchantById('echo');
    const fired = echoDef
      ? echoDef.respondsTo.some((t: EnchantTrigger) => resolveReports(report.waves, t))
      : false;
    if (!fired) return;
    const i = combat.discardPile.findIndex(c => c.uid === card.uid);
    if (i < 0) return;
    combat.discardPile.splice(i, 1);
    combat.drawPile.push(card);
    shuffleInPlace(combat.drawPile, combat.rng);
    combat.log.push({
      kind: 'enchantFired',
      enchant: 'echo',
      card: card.def.name,
      detail: 'reshuffled',
    });
  }
}

/**
 * The once-per-turn free swap: the baseline match-3 move that does not spend
 * energy or a card. Swapping can complete a match, so it resolves the board
 * exactly like a card would. False if unavailable — already used, wrong phase,
 * or the cells are not orthogonally adjacent.
 */
export function freeSwap(combat: Combat, a: CellIndex, b: CellIndex): boolean {
  if (combat.phase !== 'play' || combat.freeSwapUsed) return false;
  if (!adjacentCells(a, b)) return false;
  const ka = combat.grid[a] ?? null;
  const kb = combat.grid[b] ?? null;
  if (ka === null && kb === null) return false;
  combat.grid[a] = kb;
  combat.grid[b] = ka;
  combat.freeSwapUsed = true;
  resolveBoard(combat);
  return true;
}

/** What a play would do, for the aim preview. Deltas, not absolutes. */
export interface PreviewOutcome {
  /** HP actually removed from enemies (so enemy Block is already accounted for). */
  readonly damage: number;
  readonly block: number;
  readonly energy: number;
  readonly poison: number;
  readonly gemsCleared: number;
  /**
   * Per-wave breakdown for scoring readability. The preview shows the
   * calculation — "4 Ember → 8 DMG" — not just the total, so the player
   * learns the scoring system by making moves.
   */
  readonly waves: readonly PreviewWave[];
}

/** One cascade wave's worth of scoring, simplified for display. */
export interface PreviewWave {
  readonly cascadeMult: number;
  readonly runs: readonly { kind: GemKind; size: number; tier: string; value: number }[];
  readonly rowsCleared: number;
  readonly rowBurst: number;
  readonly emberDamage: number;
  readonly frostBlock: number;
  readonly poison: number;
  readonly energy: number;
}

/**
 * Deep-enough copy for a throwaway simulation. `rng` is shared by reference on
 * purpose: no card effect or resolver path draws from it, so previewing cannot
 * perturb the real game's random stream.
 */
export function cloneCombat(c: Combat): Combat {
  return {
    ...c,
    grid: c.grid.slice(),
    player: { ...c.player },
    enemies: c.enemies.map(e => ({ ...e })),
    drawPile: c.drawPile.slice(),
    hand: c.hand.slice(),
    discardPile: c.discardPile.slice(),
    exhaustedPile: c.exhaustedPile.slice(),
    powers: c.powers.map(p => ({ ...p })),
    gemWeights: cloneWeights(c.gemWeights),
    log: [],
    // A clone must never advance the live run's generator. Spread copies the
    // rng by reference, and preview systems clone on every frame while the
    // player is aiming — any draw on a clone would silently desync the run
    // from its seed. Previews are hypothetical; they get a throwaway stream.
    rng: seededRng(0),
  };
}

function sum(xs: readonly number[]): number {
  let n = 0;
  for (const x of xs) n += x;
  return n;
}

/**
 * Play the card on a clone and report what actually changed. This is the whole
 * simulated outcome — every cascade wave, powers, and afterResolve included —
 * because it runs the real `playCard` rather than reimplementing it. That is
 * also why the preview can never drift from the simulation.
 */
export function previewCard(
  c: Combat,
  card: CardInstance,
  target: PlayTarget = {},
): PreviewOutcome | null {
  if (!canPlay(c, card)) return null;
  const cc = cloneCombat(c);
  const inst = cc.hand.find(x => x.uid === card.uid);
  if (!inst) return null;

  const hpBefore = sum(cc.enemies.map(e => e.hp));
  const poisonBefore = sum(cc.enemies.map(e => e.poison));
  const report = playCard(cc, inst, target);
  if (!report) return null;

  // Build per-wave scoring detail for the preview overlay.
  const waves: PreviewWave[] = report.waves.map(w => ({
    cascadeMult: w.multiplier,
    runs: w.runs.map(run => {
      const tier = tierFor(run.cells.length);
      return {
        kind: run.kind,
        size: run.cells.length,
        tier,
        value: run.cells.length * tierGemMultiplier(tier),
      };
    }),
    rowsCleared: w.rowsCleared.length,
    rowBurst: w.rowBurst,
    emberDamage: w.emberDamage,
    frostBlock: w.frostBlock,
    poison: w.poison,
    energy: w.energyGained,
  }));

  return {
    damage: hpBefore - sum(cc.enemies.map(e => e.hp)),
    block: cc.player.block - c.player.block,
    energy: cc.player.energy - c.player.energy,
    poison: sum(cc.enemies.map(e => e.poison)) - poisonBefore,
    gemsCleared: report.totalGemsCleared,
    waves,
  };
}

export function executeIntent(combat: Combat, enemy: EnemyState, intent: Intent): TurnStep[] {
  const steps: TurnStep[] = [];

  switch (intent.kind) {
    case 'attack': {
      // Record what actually landed, not what was promised — a blocked hit
      // should not float a number the player never lost. `blocked` travels
      // too, so the HUD can explain why the number is smaller than the intent.
      const dealt = damagePlayer(combat, intent.amount);
      steps.push(
        step('enemyAttack', enemy.uid, combat, dealt, { blocked: intent.amount - dealt }),
      );
      break;
    }

    case 'attackTimes': {
      // One beat per hit, not one beat for the total. `ATK 4 x2` and `ATK 8`
      // are different events and must not look identical — two rapid impacts
      // make the distinction unmistakable even though resolution is atomic.
      for (let h = 0; h < intent.hits; h++) {
        if (combat.phase === 'lost') break;
        const dealt = damagePlayer(combat, intent.amount);
        steps.push(
          step('enemyAttack', enemy.uid, combat, dealt, {
            blocked: intent.amount - dealt,
            // Snappy, so a flurry reads as a flurry rather than a slow march.
            dur: STEP_DUR.enemyAttack * 0.55,
          }),
        );
      }
      break;
    }

    case 'block':
      enemy.block += intent.amount;
      combat.log.push({ kind: 'enemyBlocked', enemy: enemy.uid, amount: intent.amount });
      steps.push(step('enemyBlock', enemy.uid, combat, intent.amount));
      break;

    case 'addStone': {
      const placed = dropStones(combat.grid, intent.count, combat.rng);
      combat.log.push({ kind: 'stonesDropped', count: placed.length });
      if (placed.length > 0) {
        steps.push(
          step('stoneDrop', enemy.uid, combat, placed.length, {
            cells: placed,
            kinds: placed.map(() => 'stone' as GemKind),
          }),
        );
      }
      break;
    }

    case 'pushUpStone': {
      const destroyed = pushUpAndFillBottom(combat.grid, intent.rows, combat.rng);
      combat.log.push({ kind: 'boardPushed', rows: intent.rows, destroyed });
      steps.push(step('boardPush', enemy.uid, combat, intent.rows));
      break;
    }

    case 'hexColumn': {
      const col = hexRandomColumn(combat.grid, combat.rng);
      if (col !== null) {
        combat.log.push({ kind: 'columnHexed', column: col });
        steps.push(step('hexColumn', enemy.uid, combat, col));
      }
      break;
    }

    case 'scramble': {
      const cells = scrambleGems(combat.grid, intent.count, combat.rng);
      combat.log.push({ kind: 'gemsScrambled', count: cells.length });
      if (cells.length > 0) steps.push(step('scramble', enemy.uid, combat, cells.length));
      break;
    }

    case 'dropPiece':
      steps.push(...enemyDropPiece(combat, enemy, intent.piece));
      break;

    case 'multi':
      for (const sub of intent.intents) {
        if (combat.phase === 'lost') break;
        steps.push(...executeIntent(combat, enemy, sub));
      }
      break;
  }
  checkEnd(combat);
  return steps;
}

/**
 * End the player's turn.
 *
 * Order is deliberate: discard, then CAP CHECK, then enemies act, then new
 * intents. The cap bill is assessed against the board as the player left it —
 * so an enemy push-up during this enemy phase is never billed the same turn.
 * The Mason raises the floor and the bill arrives on the player's NEXT
 * end-of-turn. (The GDD lists cap check after enemy actions; this is the
 * sequencing the design review locked in.)
 */
export function endTurn(combat: Combat): TurnStep[] {
  const steps: TurnStep[] = [];
  if (combat.phase !== 'play') return steps;

  combat.discardPile.push(...combat.hand);
  combat.hand = [];

  combat.phase = 'cap';
  const { columns } = applyCapCheck(combat.grid);
  if (columns.length > 0) {
    const amount = capDamageFor(combat.relics, CAP_DAMAGE_PER_COLUMN) * columns.length;
    combat.log.push({ kind: 'capDamage', columns, amount });
    damagePlayer(combat, amount);
    checkEnd(combat);
    steps.push(step('capDamage', 0, combat, amount));
    if (isLost(combat)) return steps;
  }

  combat.phase = 'enemy';
  for (const enemy of combat.enemies) {
    if (enemy.hp <= 0) continue;
    // Enemy Block is a one-turn wall, exactly like the player's.
    enemy.block = 0;
    combat.log.push({ kind: 'enemyActed', enemy: enemy.uid, intent: enemy.intent });
    steps.push(...executeIntent(combat, enemy, enemy.intent));
    if (isLost(combat)) return steps;
  }

  for (const enemy of combat.enemies) {
    if (enemy.hp <= 0) continue;
    enemy.patternIndex += 1;
    const pattern = enemy.def.pattern;
    const next = pattern[enemy.patternIndex % pattern.length];
    if (next) enemy.intent = next;
  }

  // Upkeep: enemy poison ticks first, then the player's own.
  const poisonDamage = beginTurn(combat, steps);
  if (poisonDamage > 0) steps.push(step('playerPoison', 0, combat, poisonDamage));
  return steps;
}

export { idx, idxCol, idxRow };
